import { useMemo } from 'react';
import { useAppStore } from '@/store';
import { selectSubstitutionSafety } from '@/store';
import { Confidence } from '@allergen-lens/shared-logic';
import { categoryLabel } from '@allergen-lens/shared-logic';
import type { AllergenEntry } from '@allergen-lens/shared-logic';
import allergensData from '@/data/allergens.json';
import substitutionData from '@/data/substitutions.json';
import styles from './SubstitutionPanel.module.css';

const allergenLibrary = allergensData as AllergenEntry[];

function allergenIdToName(id: string): string {
  return allergenLibrary.find((a) => a.id === id)?.canonicalName ?? id;
}

interface Alternative {
  name: string;
  notes: string;
  containsAllergens?: string[];
}

interface Substitution {
  allergenCategory: string;
  allergenId: string;
  alternatives: Alternative[];
}

const substitutions = substitutionData.substitutions as Substitution[];

export function SubstitutionPanel() {
  const results = useAppStore((s) => s.results);
  const profiles = useAppStore((s) => s.profiles);
  const activeProfileId = useAppStore((s) => s.activeProfileId);

  const safety = useMemo(
    () => selectSubstitutionSafety(useAppStore.getState()),
    [profiles, activeProfileId]
  );

  const detectedAllergenIds = useMemo(
    () =>
      new Set(
        results
          .filter((r) => r.confidence === Confidence.Present || r.confidence === Confidence.Possible)
          .map((r) => r.allergenId)
      ),
    [results]
  );

  const relevantSubs = useMemo(
    () => substitutions.filter((s) => detectedAllergenIds.has(s.allergenId)),
    [detectedAllergenIds]
  );

  if (relevantSubs.length === 0) {
    return (
      <div className={styles.substitutionPanel}>
        <div className={styles.emptyState}>
          No flagged allergens to suggest substitutions for.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.substitutionPanel}>
      {relevantSubs.map((sub) => (
        <div key={sub.allergenId} className={styles.allergenSection}>
          <div className={styles.allergenHeader}>{categoryLabel(sub.allergenCategory)}</div>
          <div className={styles.altList}>
            {sub.alternatives.map((alt, i) => {
              const altSafety = safety?.[alt.name];
              return (
                <div key={i} className={styles.altItem}>
                  <div className={styles.altMain}>
                    <span className={styles.altName}>{alt.name}</span>
                    <span className={styles.altNotes}>{alt.notes}</span>
                  </div>
                  {altSafety && (
                    <span className={`${styles.safetyBadge} ${altSafety.safe ? styles.safeBadge : styles.unsafeBadge}`}>
                      {altSafety.safe
                        ? 'Safe for you'
                        : `UNSAFE — contains ${altSafety.conflictingAllergenIds
                            .map(allergenIdToName)
                            .join(', ')}`}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
